import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, AlertCircle, CheckCircle, XCircle, ArrowLeftRight } from 'lucide-react';

const returnSections = [
  {
    title: '1. Return Eligibility',
    content: [
      'Items must be returned within 7 days of receiving the order.',
      'Items must be unused, unwashed, and in original condition with all tags attached.',
      'Items must be in original packaging.',
      'Products must not be damaged due to customer misuse.',
    ],
  },
  {
    title: '2. Non-Returnable Items',
    content: [
      'Items that have been worn, washed, or altered.',
      'Items with missing tags or damaged packaging.',
      'Items damaged due to misuse by the customer.',
    ],
  },
  {
    title: '3. Cashback / Refund Process',
    content: [
      'Contact BabyCub support within 7 days of receiving the order.',
      'Provide your order number and reason for return with photos if applicable.',
      'Our team will review your request and respond within 1â€“2 business days.',
      'After approval, cashback/refund will be processed according to store policy.',
    ],
  },
  {
    title: '4. Damaged or Wrong Items',
    content: 'If you receive a damaged, defective, or wrong item, please contact us immediately at 03363898930 with photos. We will arrange a free replacement or cashback at no extra cost.',
  },
];

const exchangeSections = [
  {
    title: '5. Exchange Eligibility',
    content: [
      'Exchange requests must be made within 14 days of receiving the order.',
      'Items must be in unused, unwashed and original condition.',
      'Exchange is subject to product availability.',
      'Size, colour, or product issues are valid reasons for exchange.',
    ],
  },
  {
    title: '6. How to Request Exchange',
    content: [
      'Contact BabyCub at 03363898930 within 14 days of receiving the order.',
      'Provide your order number and the exchange details.',
      'Our team will confirm availability and provide instructions.',
    ],
  },
  {
    title: '7. Contact for Returns & Exchanges',
    content: 'For all return, cashback and exchange inquiries, call or WhatsApp BabyCub at 03363898930. We are available to assist you.',
  },
];

const eligible = ['Wrong item received', 'Defective or damaged product', 'Size issue (exchange within 14 days)', 'Product differs from description'];
const notEligible = ['Worn or washed items', 'Missing tags or packaging', 'Items damaged by customer', 'Items beyond return/exchange window'];

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-900 text-white py-14 px-4 text-center">
        <p className="text-xs tracking-[0.3em] text-blue-400 uppercase mb-3">BabyCub Policies</p>
        <h1 className="text-3xl md:text-4xl font-black">Return, Cashback & Exchange Policy</h1>
        <p className="text-gray-400 text-sm mt-3">Simple, fair and customer-friendly policies</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <div className="bg-green-50 rounded-xl p-5 text-center border border-green-100">
            <RotateCcw size={28} className="text-green-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900 text-sm">7-Day Return</p>
            <p className="text-xs text-gray-500 mt-1">Return & Cashback Policy</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 text-center border border-blue-100">
            <ArrowLeftRight size={28} className="text-blue-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900 text-sm">14-Day Exchange</p>
            <p className="text-xs text-gray-500 mt-1">Exchange Policy</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100">
            <AlertCircle size={28} className="text-gray-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900 text-sm">COD Available</p>
            <p className="text-xs text-gray-500 mt-1">Cash on Delivery</p>
          </div>
        </div>

        {/* Eligible vs Not */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
          <div className="bg-green-50 rounded-xl p-5 border border-green-100">
            <h3 className="font-bold text-green-800 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
              <CheckCircle size={16} /> Eligible for Return/Exchange
            </h3>
            <ul className="space-y-2">
              {eligible.map(item => (
                <li key={item} className="text-sm text-green-700 flex items-start gap-2">
                  <span className="mt-1 flex-shrink-0">âœ“</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-red-50 rounded-xl p-5 border border-red-100">
            <h3 className="font-bold text-red-800 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
              <XCircle size={16} /> Not Eligible
            </h3>
            <ul className="space-y-2">
              {notEligible.map(item => (
                <li key={item} className="text-sm text-red-700 flex items-start gap-2">
                  <span className="mt-1 flex-shrink-0">âœ—</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Return Policy */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-green-200">
            <RotateCcw size={22} className="text-green-600" />
            <h2 className="text-lg font-black text-gray-900">Return & Cashback Policy (7 Days)</h2>
          </div>
          <div className="space-y-6">
            {returnSections.map(section => (
              <div key={section.title}>
                <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="space-y-1.5">
                    {section.content.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                        <span className="text-green-500 mt-1 flex-shrink-0">â€¢</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600 leading-relaxed">{section.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Exchange Policy */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-blue-200">
            <ArrowLeftRight size={22} className="text-blue-600" />
            <h2 className="text-lg font-black text-gray-900">Exchange Policy (14 Days)</h2>
          </div>
          <div className="space-y-6">
            {exchangeSections.map(section => (
              <div key={section.title}>
                <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="space-y-1.5">
                    {section.content.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                        <span className="text-blue-500 mt-1 flex-shrink-0">â€¢</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600 leading-relaxed">{section.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center border-t border-gray-100 pt-10">
          <p className="text-sm text-gray-600 mb-4">Need help with a return or exchange? Contact BabyCub support.</p>
          <a href="tel:03363898930" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg hover:bg-blue-700 transition-colors mr-3">
            Call 03363898930
          </a>
          <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-900 text-gray-900 px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
            Contact Form
          </Link>
        </div>
      </div>
    </div>
  );
}
